/**
 * Plain utility functions for persisting and loading session state
 * from localStorage. No React hooks — just read/write.
 */

import type { WallGroup } from '@/stores/slices/toolSlice';

export const STORAGE_KEYS = {
  CAMERA_POSITION: 'khorium_camera_position',
  CAMERA_TARGET: 'khorium_camera_target',
  VIEW_SETTINGS: 'khorium_view_settings',
  MESH_SETTINGS: 'khorium_mesh_settings',
  LAST_PROJECT_ID: 'khorium_last_project_id',
  UI_MODE: 'khorium_ui_mode',
  SUBSCRIPTION: 'khorium_subscription',
  MESH_CELL_CACHE: 'khorium_mesh_cell_cache',
  // Per-dimension display-unit prefs (JSON: { "Pa": "psi", "m/s": "ft/s", … })
  // for SimOps result + field values — a read-only display preference that
  // survives reloads / sessions.
  UNIT_PREFS: 'khorium_unit_prefs',
  // How the SimOps DP table's Time column renders — one of the four modes
  // in `useDpTimeMode`. Right-click on the header cycles through them; the
  // choice survives reloads / sessions.
  DP_TIME_MODE: 'khorium_dp_time_mode',
  // How tightly the SimOps Analyze dashboard packs — one of the three levels
  // in `useAnalyzeDensity`. A display preference like UNIT_PREFS: it changes
  // nothing about the study, survives reloads / sessions, and is in
  // PERSIST_ACROSS_RESET below because "start fresh" is about project state.
  // `_v2` because the VALUES changed meaning, not just the set: the level
  // formerly stored as `compact` (cards with gaps) is now `comfortable`, and
  // `compact` names what used to be `dense`. Reusing the key would have read
  // every existing reader's remembered default as the tightest level. A new
  // key makes the legacy entry invisible, so they fall back to the default —
  // which is the same look they had.
  ANALYZE_DENSITY: 'khorium_analyze_density_v2',
  // Whether the hand-calc workspace's assumptions table is open. Collapsed by
  // default; a display preference like ANALYZE_DENSITY, kept across "start
  // fresh".
  HAND_CALC_ASSUMPTIONS_OPEN: 'khorium_hand_calc_assumptions_open',
  // Whether each result viewer shows its Max/Min callouts (ENG-3089), as
  // JSON `{ "fea": false, … }` — a viewer absent from the map shows them. A
  // display preference like ANALYZE_DENSITY, kept across "start fresh". See
  // `useExtremaToggle`.
  VIEWER_EXTREMA: 'khorium_viewer_extrema',
  // Per-geometry mesh-authoring drafts (enclosure/domain, overrides, face
  // tags, …) so settings survive refresh/navigation before Generate. Keyed
  // by geometry id inside the blob — see `utils/authoringDraft.ts`. Cleared
  // by `clearPersistedState` (start-fresh) since it's not in PERSIST_ACROSS_RESET.
  AUTHORING_DRAFTS: 'khorium_authoring_drafts',
  // Per-geometry face-labels server revision last reconciled with (JSON:
  // { "<geometryId>": <revision> }) — the offline-cache half of the
  // face-labels autosave (ENG-1154). See `utils/authoringDraft.ts`. Cleared
  // by `clearPersistedState` alongside AUTHORING_DRAFTS.
  FACE_LABELS_REVISIONS: 'khorium_face_labels_revisions',
  // Assistant chat panel UI state (see utils/chatPrefs.ts). All four keys
  // (open/fullscreen/sessionId/width) survive a workspace reset — chat is not
  // project-scoped — so they are all in PERSIST_ACROSS_RESET below. The
  // session-scoped keys (open/fullscreen/sessionId) are cleared via
  // `chatPrefs.clearOnLogout` at the /login session-end choke point (covers
  // logout/expiry/auth-guard) and in demo logout (width is a display pref and
  // is kept), so a shared browser never leaks one user's chat/session to the next.
  CHAT_OPEN: 'assistant.chat.open',
  CHAT_FULLSCREEN: 'assistant.chat.fullscreen',
  CHAT_SESSION_ID: 'assistant.chat.sessionId',
  CHAT_WIDTH: 'assistant.chat.width',
  // The user's keyboard-shortcut customizations (see commands/keymapStorage.ts).
  // Only OVERRIDES are stored, never the resolved keymap, so a user who
  // rebound one command still tracks DEFAULT_KEYMAP for the rest. In
  // PERSIST_ACROSS_RESET below: a rebind is a personal preference, not project
  // state, and "start fresh" must not silently undo it.
  KEYBOARD_KEYMAP: 'khorium_keyboard_keymap',
  // PREFIX, not a whole key: `utils/recentlyViewed.ts` appends `:<userId>`
  // (or `:anon`) so one user's shared-link history never shows in the next
  // user's tab on a shared browser. The suffix also keeps it out of
  // `clearPersistedState` below — this is cross-workspace history, not project
  // state, so "start fresh" must leave it alone. Not in PERSIST_ACROSS_RESET
  // for the same reason: that list only covers exact keys.
  RECENTLY_VIEWED: 'khorium_recently_viewed',
} as const;

export function safeParseJSON<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

// Face_indices in wall_groups are surface-source-bound: a face_index that names
// a cavity wall is meaningless on the body STL and vice versa. Keying the slot
// by source prevents the wallGroups from the cavity picker from painting random
// faces on the body STL when the user flips internal_flow ↔ external_flow.
export type WallGroupSource = 'body' | 'cavity';

function wallGroupsKey(geometryId: string, source: WallGroupSource): string {
  return `khorium_wall_groups_${geometryId}_${source}`;
}

export function saveWallGroups(
  geometryId: string,
  source: WallGroupSource,
  groups: WallGroup[],
): void {
  sessionStorage.setItem(
    wallGroupsKey(geometryId, source),
    JSON.stringify(groups),
  );
}

export function loadWallGroups(
  geometryId: string,
  source: WallGroupSource,
): WallGroup[] | null {
  return safeParseJSON<WallGroup[] | null>(
    sessionStorage.getItem(wallGroupsKey(geometryId, source)),
    null,
  );
}

// Per-project simulationType. Survives reloads via localStorage. Reopening a
// geometry that has only CFD meshes in history would otherwise re-derive
// `simulationType` from the latest mesh's `domain.mode` in useInitialProjectLoad
// and clobber a FEA selection the user made after the last mesh ran.
type SimulationType = 'cfd' | 'fea' | 'cht';
function projectSimTypeKey(geometryId: string): string {
  return `khorium_sim_type_${geometryId}`;
}
export function saveProjectSimulationType(
  geometryId: string,
  type: SimulationType,
): void {
  localStorage.setItem(projectSimTypeKey(geometryId), type);
}
export function loadProjectSimulationType(
  geometryId: string,
): SimulationType | null {
  const raw = localStorage.getItem(projectSimTypeKey(geometryId));
  return raw === 'cfd' || raw === 'fea' || raw === 'cht' ? raw : null;
}

export function saveViewSettings(settings: Record<string, unknown>): void {
  localStorage.setItem(STORAGE_KEYS.VIEW_SETTINGS, JSON.stringify(settings));
}

/**
 * Read and parse a JSON localStorage key, falling back on ANY failure.
 *
 * The guard wraps `getItem` as well as the parse: a browser that blocks site
 * data throws on the read itself, which `safeParseJSON` never sees. Callers
 * run inside mount effects, where an uncaught throw takes the whole workspace
 * to the error boundary.
 */
export function safeReadJSON<T>(key: string, fallback: T): T {
  try {
    return safeParseJSON<T>(localStorage.getItem(key), fallback);
  } catch {
    return fallback;
  }
}

/**
 * The persisted mesh settings, or `{}` when there are none. A stored literal
 * `"null"` parses successfully to `null`, so the object check is load-bearing
 * — without it a bare property read on the result throws.
 */
export function loadMeshSettings(): Record<string, unknown> {
  const parsed = safeReadJSON<unknown>(STORAGE_KEYS.MESH_SETTINGS, null);
  return typeof parsed === 'object' && parsed !== null
    ? (parsed as Record<string, unknown>)
    : {};
}

export function saveMeshSettings(settings: Record<string, unknown>): void {
  localStorage.setItem(STORAGE_KEYS.MESH_SETTINGS, JSON.stringify(settings));
}

export function saveCameraPosition(
  position: { x: number; y: number; z: number } | null,
  target: { x: number; y: number; z: number } | null,
): void {
  if (position) {
    localStorage.setItem(
      STORAGE_KEYS.CAMERA_POSITION,
      JSON.stringify({ x: position.x, y: position.y, z: position.z }),
    );
  }
  if (target) {
    localStorage.setItem(
      STORAGE_KEYS.CAMERA_TARGET,
      JSON.stringify({ x: target.x, y: target.y, z: target.z }),
    );
  }
}

export function saveLastProjectId(projectId: string | null): void {
  if (projectId) {
    localStorage.setItem(STORAGE_KEYS.LAST_PROJECT_ID, projectId);
    return;
  }
  localStorage.removeItem(STORAGE_KEYS.LAST_PROJECT_ID);
}

export function saveUiMode(mode: 'single' | 'batch'): void {
  if (mode === 'batch' || mode === 'single') {
    localStorage.setItem(STORAGE_KEYS.UI_MODE, mode);
  }
}

export function loadPersistedState() {
  return {
    viewSettings: safeParseJSON(
      localStorage.getItem(STORAGE_KEYS.VIEW_SETTINGS),
      null,
    ),
    meshSettings: safeParseJSON(
      localStorage.getItem(STORAGE_KEYS.MESH_SETTINGS),
      null,
    ),
    cameraPosition: safeParseJSON(
      localStorage.getItem(STORAGE_KEYS.CAMERA_POSITION),
      null,
    ),
    cameraTarget: safeParseJSON(
      localStorage.getItem(STORAGE_KEYS.CAMERA_TARGET),
      null,
    ),
    lastProjectId: localStorage.getItem(STORAGE_KEYS.LAST_PROJECT_ID),
  };
}

// --- Mesh cell count prediction cache ---
// Stores actual cell counts from completed mesh jobs.
// Grouped by geometry + element type so any previous run on this geometry
// can calibrate predictions for different mesh size settings.

export type MeshCellRun = {
  cells: number; // actual numElements3d from completed job
  minSizeMm: number;
  maxSizeMm: number;
  ts: number;
};

type MeshCellCache = Record<string, MeshCellRun[]>; // key = geometryId|elementType

function meshRunsKey(geometryId: string, elementType: string): string {
  return `${geometryId}|${elementType}`;
}

export function saveMeshCellCount(
  geometryId: string,
  elementType: string,
  minSizeMm: number,
  maxSizeMm: number,
  cells: number,
): void {
  const cache = safeParseJSON<MeshCellCache>(
    localStorage.getItem(STORAGE_KEYS.MESH_CELL_CACHE),
    {},
  );
  const key = meshRunsKey(geometryId, elementType);
  const runs = cache[key] ?? [];
  // Replace existing entry for the same settings, or append.
  const idx = runs.findIndex(
    (r) => r.minSizeMm === minSizeMm && r.maxSizeMm === maxSizeMm,
  );
  const entry: MeshCellRun = { cells, minSizeMm, maxSizeMm, ts: Date.now() };
  if (idx >= 0) runs[idx] = entry;
  else runs.push(entry);
  cache[key] = runs;
  localStorage.setItem(STORAGE_KEYS.MESH_CELL_CACHE, JSON.stringify(cache));
}

/** Returns all completed runs for a geometry, most recent first. */
export function lookupMeshRuns(
  geometryId: string,
  elementType: string,
): MeshCellRun[] {
  const cache = safeParseJSON<MeshCellCache>(
    localStorage.getItem(STORAGE_KEYS.MESH_CELL_CACHE),
    {},
  );
  const runs = cache[meshRunsKey(geometryId, elementType)] ?? [];
  return [...runs].sort((a, b) => b.ts - a.ts);
}

const PERSIST_ACROSS_RESET = new Set<string>([
  STORAGE_KEYS.SUBSCRIPTION,
  STORAGE_KEYS.MESH_CELL_CACHE,
  STORAGE_KEYS.UNIT_PREFS,
  STORAGE_KEYS.ANALYZE_DENSITY,
  STORAGE_KEYS.HAND_CALC_ASSUMPTIONS_OPEN,
  STORAGE_KEYS.VIEWER_EXTREMA,
  STORAGE_KEYS.CHAT_OPEN,
  STORAGE_KEYS.CHAT_FULLSCREEN,
  STORAGE_KEYS.CHAT_SESSION_ID,
  STORAGE_KEYS.CHAT_WIDTH,
  STORAGE_KEYS.KEYBOARD_KEYMAP,
]);

export function clearPersistedState(): void {
  Object.values(STORAGE_KEYS).forEach((key) => {
    if (!PERSIST_ACROSS_RESET.has(key)) {
      localStorage.removeItem(key);
    }
  });
}
